import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../Common/Header';
import { api } from '../../services/api';
import config from '../../config/environment';
import './Nominations.css';

// ==================== PARTY COLOR MAPPING ====================
const PARTY_COLORS = {
  'BJP': { primary: '#FF9933', secondary: '#138808' },
  'INC': { primary: '#19AAED', secondary: '#0D6EAF' },
  'JDU': { primary: '#006400', secondary: '#228B22' },
  'JD(U)': { primary: '#006400', secondary: '#228B22' },
  'RJD': { primary: '#008000', secondary: '#006400' },
  'AIMIM': { primary: '#00A100', secondary: '#008000' },
  'AAP': { primary: '#0066B3', secondary: '#004C8C' },
  'BSP': { primary: '#2B57AB', secondary: '#1F3F7A' },
  'CPI': { primary: '#FF0000', secondary: '#CC0000' },
  'CPI(M)': { primary: '#CC0000', secondary: '#990000' },
  'CPM': { primary: '#CC0000', secondary: '#990000' },
  'SP': { primary: '#FF2222', secondary: '#CC0000' },
  'NOTA': { primary: '#666666', secondary: '#444444' },
  'IND': { primary: '#808080', secondary: '#666666' },
  'DEFAULT': { primary: '#667eea', secondary: '#764ba2' }
};

// ==================== PRIORITY PARTIES LIST ====================
const PRIORITY_PARTIES = [
  'AAP', 'BJP', 'CPI', 'CPI (M)', 'CPI(M)', 'CPM', 'HAM', 'INC',
  'JD(U)', 'JDU', 'JJD', 'JSP', 'LJP (R)', 'LJP(R)', 'RJD', 'RLSP', 'VIP'
];

// ==================== MAIN NOMINATIONS COMPONENT ====================
const Nominations = () => {
  const [nominations, setNominations] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [parties, setParties] = useState([]);
  const [filters, setFilters] = useState({ constituency: '', party: '', search: '' });
  const [stats, setStats] = useState({ totalConstituencies: 0, totalCandidates: 0, avgPerSeat: 0 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('candidates');
  
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => { loadNominations(); }, []);
  useEffect(() => { applyFilters(); }, [filters, nominations]);

  const loadNominations = async () => {
    try {
      console.log('📥 Loading nominations...');
      const data = await api.getNominationsByConstituency();
      console.log('✅ Nominations loaded:', data);
      setNominations(data.constituencies);
      setFilteredData(data.constituencies);
      
      const uniqueConstituencies = data.constituencies.map(c => ({
        id: c.constituencyId,
        name: `${c.constituencyCode} - ${c.constituencyName}`
      }));
      setConstituencies(uniqueConstituencies);

      const uniqueParties = new Set();
      data.constituencies.forEach(c => {
        c.candidates.forEach(ca => uniqueParties.add(ca.partyShortName));
      });

      const allParties = [...uniqueParties];
      const priorityParties = PRIORITY_PARTIES.filter(p => allParties.includes(p));
      const otherParties = allParties.filter(p => !PRIORITY_PARTIES.includes(p)).sort();
      setParties([...priorityParties, ...otherParties]);

      setStats({
        totalConstituencies: data.totalConstituencies,
        totalCandidates: data.totalCandidates,
        avgPerSeat: (data.totalCandidates / data.totalConstituencies).toFixed(1)
      });
    } catch (error) {
      console.error('❌ Error loading nominations:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    console.log('🔍 Applying filters:', filters);
    let filtered = [...nominations];

    if (filters.constituency) {
      filtered = filtered.filter(c => c.constituencyId === parseInt(filters.constituency));
      console.log(`📍 After constituency filter: ${filtered.length} constituencies`);
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered
        .map(constituency => {
          const constituencyMatches = 
            constituency.constituencyName?.toLowerCase().includes(searchLower) ||
            constituency.constituencyCode?.toLowerCase().includes(searchLower) ||
            constituency.district?.toLowerCase().includes(searchLower);

          const matchedCandidates = constituency.candidates.filter(ca => {
            return (
              ca.candidateName?.toLowerCase().includes(searchLower) ||
              ca.partyName?.toLowerCase().includes(searchLower) ||
              ca.partyShortName?.toLowerCase().includes(searchLower) ||
              ca.education?.toLowerCase().includes(searchLower) ||
              ca.profession?.toLowerCase().includes(searchLower)
            );
          });

          if (constituencyMatches) return constituency;
          else if (matchedCandidates.length > 0) return { ...constituency, candidates: matchedCandidates };
          else return null;
        })
        .filter(c => c !== null && c.candidates.length > 0);
      console.log(`🔎 After search filter: ${filtered.length} constituencies`);
    }

    if (filters.party && !filters.search) {
      filtered = filtered
        .map(c => ({ ...c, candidates: c.candidates.filter(ca => ca.partyShortName === filters.party) }))
        .filter(c => c.candidates.length > 0);
      console.log(`🎯 After party filter: ${filtered.length} constituencies`);
    }

    setFilteredData(filtered);
    console.log('✅ Final filtered data:', filtered.length, 'constituencies');
  };

  const handleFilterChange = (name, value) => {
    console.log(`🔄 Filter changed: ${name} = ${value}`);
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleStatBoxClick = (type) => {
    console.log('📊 Stat box clicked:', type);
    if (type === 'constituencies') {
      console.log('🏛️ Showing constituencies view');
      setViewMode('constituencies');
      setFilters({ constituency: '', party: '', search: '' });
    } else if (type === 'candidates') {
      console.log('👥 Showing candidates view');
      setViewMode('candidates');
      setFilters({ constituency: '', party: '', search: '' });
    }
    setTimeout(() => {
      const resultsSection = document.querySelector('.nom-results-section');
      if (resultsSection) {
        resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const viewCandidateDetails = (candidateId) => {
    console.log('👤 Viewing candidate:', candidateId);
    navigate(`/candidate/${candidateId}`);
  };

  const getPartyColor = (partyShortName) => {
    return PARTY_COLORS[partyShortName] || PARTY_COLORS['DEFAULT'];
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
    const baseUrl = config.API_URL.replace(/\/api$/, '');
    return `${baseUrl}${url}`;
  };

  const clearFilters = () => {
    setFilters({ constituency: '', party: '', search: '' });
  };

  return (
    <div className="nom-nominations-page">
      <Header />

      <div className="nom-page-header">
        <div className="nom-page-header-content">
          <h1>
            <i className="fas fa-users"></i>
            {t('nominations.title') || 'Candidate Nominations'}
          </h1>
          <p>{t('nominations.subtitle') || 'All Candidates by Constituency'}</p>
        </div>
      </div>

      <div className="nom-nominations-container">
        <div className="nom-stats-row">
          <div 
            className="nom-stat-box nom-clickable" 
            onClick={() => handleStatBoxClick('constituencies')}
            title={t('nominations.viewAllConstituencies') || 'View all constituencies'}
          >
            <div className="nom-stat-icon">
              <i className="fas fa-map-marked-alt"></i>
            </div>
            <h3>{t('nominations.totalConstituencies') || 'Constituencies'}</h3>
            <div className="nom-number">{stats.totalConstituencies || 0}</div>
            <div className="nom-stat-action">
              <i className="fas fa-arrow-right"></i>
            </div>
          </div>

          <div 
            className="nom-stat-box nom-clickable" 
            onClick={() => handleStatBoxClick('candidates')}
            title={t('nominations.viewAllCandidates') || 'View all candidates'}
          >
            <div className="nom-stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <h3>{t('nominations.totalCandidates') || 'Candidates'}</h3>
            <div className="nom-number">{stats.totalCandidates || 0}</div>
            <div className="nom-stat-action">
              <i className="fas fa-arrow-right"></i>
            </div>
          </div>

          <div className="nom-stat-box">
            <div className="nom-stat-icon">
              <i className="fas fa-chart-bar"></i>
            </div>
            <h3>{t('nominations.avgPerSeat') || 'Avg per Seat'}</h3>
            <div className="nom-number">{stats.avgPerSeat || 0}</div>
          </div>
        </div>

        {viewMode === 'candidates' && (
          <div className="nom-filters-section">
            <div className="nom-filters-grid">
              <div className="nom-filter-group">
                <label>
                  <i className="fas fa-map-marker-alt"></i>
                  {t('results.constituency') || 'Constituency'}
                </label>
                <select
                  value={filters.constituency}
                  onChange={(e) => handleFilterChange('constituency', e.target.value)}
                  className="nom-filter-select"
                >
                  <option value="">{t('nominations.allConstituencies') || 'All Constituencies'}</option>
                  {constituencies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="nom-filter-group">
                <label>
                  <i className="fas fa-flag"></i>
                  {t('results.party') || 'Party'}
                </label>
                <select
                  value={filters.party}
                  onChange={(e) => handleFilterChange('party', e.target.value)}
                  className="nom-filter-select"
                >
                  <option value="">{t('nominations.allParties') || 'All Parties'}</option>
                  {parties.map(party => (
                    <option key={party} value={party}>{party}</option>
                  ))}
                </select>
              </div>

              <div className="nom-filter-group nom-search-box">
                <label>
                  <i className="fas fa-search"></i>
                  {t('common.search') || 'Search'}
                </label>
                <div className="nom-search-input-wrapper">
                  <input
                    type="text"
                    placeholder={t('nominations.search') || 'Search candidate, party, or constituency...'}
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="nom-search-input"
                  />
                  {filters.search && (
                    <button 
                      className="nom-clear-search-btn"
                      onClick={() => handleFilterChange('search', '')}
                      title={t('common.clear') || 'Clear search'}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {(filters.constituency || filters.party || filters.search) && (
              <div className="nom-active-filters">
                <span className="nom-filters-label">
                  <i className="fas fa-filter"></i>
                  {t('common.activeFilters') || 'Active Filters'}:
                </span>
                {filters.constituency && (
                  <span className="nom-filter-tag">
                    {constituencies.find(c => c.id === parseInt(filters.constituency))?.name}
                    <button onClick={() => handleFilterChange('constituency', '')}>×</button>
                  </span>
                )}
                {filters.party && (
                  <span className="nom-filter-tag">
                    {filters.party}
                    <button onClick={() => handleFilterChange('party', '')}>×</button>
                  </span>
                )}
                {filters.search && (
                  <span className="nom-filter-tag">
                    "{filters.search}"
                    <button onClick={() => handleFilterChange('search', '')}>×</button>
                  </span>
                )}
                <button className="nom-clear-all-btn" onClick={clearFilters}>
                  <i className="fas fa-times-circle"></i>
                  {t('common.clearAll') || 'Clear All'}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="nom-results-section">
          {loading ? (
            <div className="nom-loading-spinner">
              <div className="nom-spinner"></div>
              <p>{t('common.loading') || 'Loading...'}</p>
            </div>
          ) : viewMode === 'constituencies' ? (
            <>
              <div className="nom-results-count">
                <p>
                  <strong>{constituencies.length}</strong>
                  {' '}
                  {t('nominations.constituencies') || 'Constituencies'}
                </p>
              </div>

              <div className="nom-constituencies-grid">
                {constituencies && constituencies.length > 0 ? (
                  constituencies.map((constituency) => (
                    <div 
                      key={constituency.id}
                      className="nom-constituency-box"
                      onClick={() => {
                        handleFilterChange('constituency', constituency.id);
                        setViewMode('candidates');
                        setTimeout(() => {
                          const resultsSection = document.querySelector('.nom-results-section');
                          if (resultsSection) {
                            resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                      }}
                    >
                      <div className="nom-constituency-box-header">
                        <i className="fas fa-location-dot"></i>
                        <h3>{constituency.name}</h3>
                      </div>
                      <div className="nom-constituency-box-arrow">
                        <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="nom-empty-state">
                    <i className="fas fa-map-location"></i>
                    <p>{t('common.noData') || 'No constituencies found'}</p>
                  </div>
                )}
              </div>
            </>
          ) : filteredData.length === 0 ? (
            <div className="nom-empty-state">
              <i className="fas fa-inbox"></i>
              <p>{t('common.noData') || 'No candidates found'}</p>
              <p className="nom-empty-hint">
                {filters.search || filters.party || filters.constituency
                  ? t('common.tryDifferentFilters') || 'Try different filters or search terms'
                  : t('common.noCandidatesYet') || 'No candidates available yet'}
              </p>
              {(filters.search || filters.party || filters.constituency) && (
                <button className="nom-reset-filters-btn" onClick={clearFilters}>
                  <i className="fas fa-undo"></i>
                  {t('common.resetFilters') || 'Reset Filters'}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="nom-results-count">
                <p>
                  <strong>{filteredData.reduce((sum, c) => sum + c.candidates.length, 0)}</strong>
                  {' '}
                  {t('nominations.candidates') || 'candidates'} 
                  {' '}
                  {t('common.in') || 'in'} 
                  {' '}
                  <strong>{filteredData.length}</strong>
                  {' '}
                  {t('nominations.constituencies') || 'constituencies'}
                </p>
              </div>
              
              <div className="nom-candidates-grid">
                {filteredData.map((constituency) => (
                  constituency.candidates.map((candidate, cIdx) => (
                    <CandidateCard
                      key={`${constituency.constituencyId}-${candidate.candidateId}-${cIdx}`}
                      candidate={candidate}
                      constituency={constituency}
                      onViewDetails={viewCandidateDetails}
                      partyColor={getPartyColor(candidate.partyShortName)}
                      getFullImageUrl={getFullImageUrl}
                      t={t}
                    />
                  ))
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== CANDIDATE CARD COMPONENT ====================
const CandidateCard = ({ candidate, constituency, onViewDetails, partyColor, getFullImageUrl, t }) => {
  const [imageError, setImageError] = useState(false);
  const [symbolError, setSymbolError] = useState(false);
  
  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  const getSymbolUrl = () => {
    return candidate.customSymbolUrl || candidate.symbolUrl || candidate.partySymbolUrl || null;
  };

  return (
    <div 
      className="nom-candidate-card" 
      onClick={() => onViewDetails(candidate.candidateId)}
      style={{ borderLeftColor: partyColor.primary, cursor: 'pointer' }}
    >
      <div className="nom-card-header">
        <div 
          className="nom-candidate-photo"
          style={{
            background: `linear-gradient(135deg, ${partyColor.primary}, ${partyColor.secondary})`
          }}
        >
          {candidate.photoUrl && !imageError ? (
            <img 
              src={getFullImageUrl(candidate.photoUrl)} 
              alt={candidate.candidateName}
              onError={() => setImageError(true)}
            />
          ) : (
            <span className="nom-initials">{getInitials(candidate.candidateName)}</span>
          )}
        </div>

        <div className="nom-candidate-info">
          <h3>{candidate.candidateName}</h3>
          <div className="nom-constituency-name">
            {constituency.constituencyCode} - {constituency.constituencyName}
          </div>
          <div className="nom-district-name">
            {t('nominations.district') || 'District'}: {constituency.district || 'N/A'}
          </div>
        </div>
      </div>

      <div className="nom-party-info-bar" style={{
        background: `linear-gradient(135deg, ${partyColor.primary}, ${partyColor.secondary})`
      }}>
        <div className="nom-party-left">
          <div className="nom-party-badge">{candidate.partyShortName}</div>
          <div className="nom-party-full-name">{candidate.partyName}</div>
        </div>
        {getSymbolUrl() && !symbolError && (
          <div className="nom-party-symbol-badge">
            <img 
              src={getFullImageUrl(getSymbolUrl())} 
              alt={`${candidate.partyShortName} symbol`}
              onError={() => setSymbolError(true)}
            />
          </div>
        )}
      </div>

      <div className="nom-card-divider"></div>

      <div className="nom-candidate-details">
        <div className="nom-detail-item">
          <span className="nom-detail-label">
            <i className="fas fa-birthday-cake"></i>
            {t('results.age') || 'Age'}
          </span>
          <span className="nom-detail-value">{candidate.age || 'N/A'}</span>
        </div>
        <div className="nom-detail-item">
          <span className="nom-detail-label">
            <i className="fas fa-graduation-cap"></i>
            {t('results.education') || 'Education'}
          </span>
          <span className="nom-detail-value">{candidate.education || 'N/A'}</span>
        </div>
        <div className="nom-detail-item">
          <span className="nom-detail-label">
            <i className="fas fa-briefcase"></i>
            {t('results.profession') || 'Profession'}
          </span>
          <span className="nom-detail-value">{candidate.profession || 'N/A'}</span>
        </div>
        <div className="nom-detail-item">
          <span className="nom-detail-label">
            <i className="fas fa-money-bill-wave"></i>
            {t('results.assets') || 'Assets'}
          </span>
          <span className="nom-detail-value">
            {candidate.assets ? `₹${candidate.assets.toLocaleString()}` : 'N/A'}
          </span>
        </div>
        <div className="nom-detail-item">
          <span className="nom-detail-label">
            <i className="fas fa-gavel"></i>
            {t('results.criminalCases') || 'Criminal Cases'}
          </span>
          <span className={`nom-criminal-badge ${candidate.criminalCases > 0 ? '' : 'nom-no-cases'}`}>
            {candidate.criminalCases || 0} {candidate.criminalCases > 0 ? '' : '✓'}
          </span>
        </div>
      </div>

      <button 
        className="nom-view-details-btn"
        style={{
          background: `linear-gradient(135deg, ${partyColor.primary}, ${partyColor.secondary})`
        }}
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails(candidate.candidateId);
        }}
      >
        <i className="fas fa-info-circle"></i>
        {t('results.viewDetails') || 'View Details'}
      </button>
    </div>
  );
};

export default Nominations;
